import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { getI18nStrings } from '../data/i18n';
import { getPost } from '../api/posts';
import { getUserPostsPath } from '../config/env';
import './LanguageNoticeBanner.css';

const LANGUAGE_NOTICE_DISMISSED_KEY = 'language-notice-dismissed';

/**
 * 言語案内バナーを非表示にし、localStorage に永続化する
 */
function persistDismissed(): void {
  try {
    localStorage.setItem(LANGUAGE_NOTICE_DISMISSED_KEY, '1');
  } catch {
    // ストレージが無効な場合は無視
  }
}

/**
 * 言語案内バナーコンポーネント
 * ブラウザの言語設定と現在のページ言語が異なる場合に案内を表示。
 * 一度閉じるか、バナー上の言語切り替えで遷移した場合は再表示しない（localStorage で永続化）。
 */
const LanguageNoticeBanner: React.FC = () => {
  const lang = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const i18n = getI18nStrings(lang);
  const postsPath = getUserPostsPath();
  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LANGUAGE_NOTICE_DISMISSED_KEY) !== null;
    } catch {
      return false;
    }
  });

  const handleDismiss = () => {
    persistDismissed();
    setIsDismissed(true);
  };

  // ブラウザの言語設定を取得
  const browserLang = navigator.language || navigator.languages?.[0] || 'en';
  const langCode = browserLang.toLowerCase().split('-')[0];
  const browserLangCode = langCode === 'ja' ? 'ja' : 'en';

  // ブラウザの言語と現在のページ言語が異なる場合のみ表示
  const shouldDisplay = browserLangCode !== lang && !isDismissed;

  const handleSwitch = async (e: React.MouseEvent) => {
    e.preventDefault();
    persistDismissed();
    setIsDismissed(true);

    const targetLang = lang === 'ja' ? 'en' : 'ja';

    // 記事詳細ページの判定
    const escapedPostsPath = postsPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const postDetailPattern = new RegExp(`^/${lang}${escapedPostsPath}/([^/?#]+)$`);
    const match = location.pathname.match(postDetailPattern);

    if (match && match[1]) {
      // 記事詳細ページの場合
      const slug = decodeURIComponent(match[1]);
      try {
        // 同じslugで別言語版の記事を取得
        await getPost(slug, targetLang);
        // 取得できた場合は、同じslugで別言語版のURLを生成
        navigate(`/${targetLang}${postsPath}/${slug}${location.search}${location.hash}`);
        return;
      } catch (err) {
        // 記事が見つからない（別言語版がない）場合は、ホームページに遷移
        navigate(`/${targetLang}${location.search}${location.hash}`);
        return;
      }
    }

    // その他のページの場合
    const currentPath = location.pathname;
    let pathWithoutLang = currentPath.replace(/^\/[^/]+/, '') || '/';
    if (pathWithoutLang === '/') {
      pathWithoutLang = '';
    }
    const targetPath = `/${targetLang}${pathWithoutLang}${location.search}${location.hash}`;
    navigate(targetPath);
  };

  if (!shouldDisplay) {
    return null;
  }

  const targetLang = lang === 'ja' ? 'en' : 'ja';
  const message = targetLang === 'ja' 
    ? i18n.languageNotice.japaneseAvailable 
    : i18n.languageNotice.englishAvailable;
  const switchText = targetLang === 'ja'
    ? i18n.languageNotice.switchToJapanese
    : i18n.languageNotice.switchToEnglish;

  return (
    <div className="language-notice-banner">
      <div className="language-notice-banner__content">
        <span className="language-notice-banner__message">{message}</span>
        <button
          onClick={handleSwitch}
          className="language-notice-banner__switch-button"
        >
          {switchText}
        </button>
        <button
          onClick={handleDismiss}
          className="language-notice-banner__close-button"
          aria-label={i18n.languageNotice.close}
          title={i18n.languageNotice.close}
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default LanguageNoticeBanner;
